import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { X, Binoculars, CheckCircle2 } from 'lucide-react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { cn } from '../lib/utils';
import { calculateOVR, blendScoutingOVR } from '../lib/utils';
import { calculateXOVR } from '../lib/xovr-engine';

interface ScoutingWizardProps {
  player: any;
  team: any;
  currentUserId: string;
  onClose: () => void;
}

const ATTRIBUTE_LABELS: Record<string, Record<string, string>> = {
  hitting:     { contact: 'Contact', power: 'Power', eye: 'Eye / Plate Discipline', approach: 'Approach' },
  fielding:    { glove: 'Glove', arm: 'Arm Strength', block: 'Blocking' },
  intangibles: { speed: 'Speed', iq: 'Baseball IQ', clutch: 'Clutch' },
  pitching:    { velocity: 'Velocity', control: 'Control' },
};

const DEFAULT_ATTRS = {
  hitting:     { contact: 3, power: 3, eye: 3, approach: 3 },
  fielding:    { glove: 3, arm: 3, block: 3 },
  intangibles: { speed: 3, iq: 3, clutch: 3 },
  pitching:    { velocity: 3, control: 3 },
};

export const ScoutingWizard = ({ player, team, currentUserId, onClose }: ScoutingWizardProps) => {
  const existing = player.assistantRatings?.[currentUserId]?.attributes;
  const [attributes, setAttributes] = useState(existing ?? DEFAULT_ATTRS);
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCatcher = player.primaryPosition === 'C';
  const isPitcher = !!(player.attributes?.pitching?.velocity > 0 || player.attributes?.pitching?.control > 0);

  const setAttr = (category: string, key: string, val: number) => {
    setAttributes((prev: any) => ({
      ...prev,
      [category]: { ...prev[category], [key]: val },
    }));
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const attrToSave = isPitcher
        ? attributes
        : { ...attributes, pitching: { velocity: 0, control: 0 } };

      const assistantOVR = calculateOVR(attrToSave, player.primaryPosition);
      const submittedAt = new Date().toISOString();

      const updatedAssistantRatings = {
        ...(player.assistantRatings ?? {}),
        [currentUserId]: { attributes: attrToSave, ovr: assistantOVR, submittedAt },
      };

      const playerRef = doc(db, `teams/${team.id}/players`, player.id);
      const saveData: any = {
        assistantRatings: updatedAssistantRatings,
      };

      if (team.settings?.includeAssistantScouting) {
        const headCoachOVR = calculateOVR(player.attributes, player.primaryPosition);
        const blended = blendScoutingOVR(headCoachOVR, updatedAssistantRatings, true);
        const breakdown = calculateXOVR(blended, player.seasonSnapshot?.stats ?? player.stats ?? null);
        saveData.ovr = breakdown.xovr;
        saveData.ovrBreakdown = breakdown;
      }

      await setDoc(playerRef, saveData, { merge: true });
      setIsSuccess(true);
    } catch (err: any) {
      console.error('Failed to save scouting report', err);
      setError('Failed to save. Check your connection and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const renderAttributeRow = (category: string, key: string) => {
    const val = (attributes as any)[category]?.[key] ?? 3;
    return (
      <div key={key} className="flex items-center justify-between bg-surface/60 px-3 py-2.5 rounded-xl">
        <span className="text-xs font-semibold uppercase text-ink-2 tracking-wide">
          {ATTRIBUTE_LABELS[category]?.[key] ?? key}
        </span>
        <div className="flex items-center gap-1.5">
          {[1, 2, 3, 4, 5].map(num => (
            <button
              key={num}
              type="button"
              onClick={() => setAttr(category, key, num)}
              className={cn(
                "w-8 h-8 rounded-lg text-[11px] font-black transition-all",
                val === num ? "bg-ink text-surface scale-110" : "bg-raised text-ink-2 hover:bg-hairline"
              )}
            >
              {num}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-surface backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.95, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.95, y: 20, opacity: 0 }}
        className="bg-canvas w-full max-w-md rounded-3xl border border-hairline shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Binoculars className="w-4 h-4 text-accent-ink" />
            <div>
              <h2 className="font-black uppercase text-sm text-ink leading-none">
                Scout #{player.number} {player.name}
              </h2>
              <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest mt-0.5">
                {player.primaryPosition} · Your blind scouting report
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-ink-2 hover:text-ink transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isSuccess ? (
          <div className="px-6 pb-8 pt-4 flex flex-col items-center text-center space-y-4">
            <CheckCircle2 className="w-12 h-12 text-accent-ink" />
            <div>
              <p className="font-black uppercase text-sm text-ink">Report Submitted</p>
              <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mt-1">
                Your scouting report has been saved.
              </p>
            </div>
            <button
              onClick={onClose}
              className="mt-2 px-6 py-2 bg-ink text-surface rounded-xl font-black text-xs uppercase tracking-widest hover:bg-ink transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-y-auto flex-1 px-6 pb-4 space-y-5">
              {/* Hitting */}
              <div className="space-y-2">
                <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest pt-2">Hitting</p>
                {Object.keys(ATTRIBUTE_LABELS.hitting).map(key => renderAttributeRow('hitting', key))}
              </div>

              {/* Fielding */}
              <div className="space-y-2">
                <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest">Fielding</p>
                {Object.keys(ATTRIBUTE_LABELS.fielding)
                  .filter(key => key !== 'block' || isCatcher)
                  .map(key => renderAttributeRow('fielding', key))}
              </div>

              {/* Pitching — only if pitcher */}
              {isPitcher && (
                <div className="space-y-2">
                  <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest">Pitching</p>
                  {Object.keys(ATTRIBUTE_LABELS.pitching).map(key => renderAttributeRow('pitching', key))}
                </div>
              )}

              {/* Intangibles */}
              <div className="space-y-2">
                <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest">Intangibles</p>
                {Object.keys(ATTRIBUTE_LABELS.intangibles).map(key => renderAttributeRow('intangibles', key))}
              </div>

              {error && (
                <p className="text-[10px] font-semibold text-danger uppercase tracking-widest">{error}</p>
              )}
            </div>

            <div className="px-6 pb-6 pt-2 flex-shrink-0 border-t border-hairline">
              <button
                onClick={handleSubmit}
                disabled={isSaving}
                className="w-full py-3 bg-ink hover:bg-ink disabled:opacity-50 text-surface rounded-xl font-black text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
              >
                {isSaving && <div className="w-4 h-4 border-2 border-surface/30 border-t-black rounded-full animate-spin" />}
                {isSaving ? 'Saving...' : existing ? 'Update Report' : 'Submit Report'}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>,
    document.body
  );
};
